import type { ScheduleState } from '../lib/schedule';

export type FieldDataType = 'decimal' | 'int' | 'text' | 'select' | 'photo' | 'video';
export interface TemplateField {
  key: string;
  label: string;
  unit: string;
  type: FieldDataType;
  required?: boolean;
  sanityMin?: number | null;
  sanityMax?: number | null;
  /** dataType 'select' only — the fixed list of choices. */
  options?: string[];
}
export interface TemplateSection {
  key: string;
  label: string;
  fields: TemplateField[];
}
export interface Template {
  templateId: string;
  title: string;
  version: string;
  sections: TemplateSection[];
}

export interface EquipmentRef {
  equipmentId: string;
  name: string;
  location: string;
  templateId: string;
}

export interface LastReading {
  submissionId: string;
  effectiveReadingAt: string;
  operatorName: string;
  abnormalStatus: 'normal' | 'warning' | 'critical';
  source: 'online' | 'offline';
}

export interface AbnormalFlag {
  field: string;
  value: number;
  severity: 'warning' | 'critical';
  normalMin: number | null;
  normalMax: number | null;
}
export interface AbnormalReport {
  status: 'normal' | 'warning' | 'critical';
  fields: AbnormalFlag[];
}

export interface PublicConfig {
  minIntervalMin: number;
  targetIntervalMin: number;
  dueGraceMin: number;
  overdueAfterMin: number;
  timezone: string;
  sessionHours: number;
  appMinVersion: string;
  /** base64url VAPID public key for push subscription; '' = push not configured yet. */
  vapidPublicKey: string;
  /** 'day 1' of the rotating PMS calendar (yyyy-MM-dd). */
  pmsCycleAnchorDate: string;
  /** the PMS rotation repeats every N days. */
  pmsCycleLengthDays: number;
}

export interface ApiErr {
  ok: false;
  error: string;
  message: string;
  schedule?: ScheduleState;
  abnormal?: AbnormalReport;
  problems?: string[];
  now?: string;
}

export interface TagResolveOk {
  ok: true;
  now: string;
  tag: { code: string };
  equipment: EquipmentRef;
  template: Template;
  schedule: ScheduleState;
  lastReading: LastReading | null;
  lastValues?: Record<string, number | string>;
}
export type TagResolveResponse = TagResolveOk | ApiErr;

export interface LoginOk {
  ok: true;
  now: string;
  token: string;
  operator: { id: string; name: string; role: 'operator' | 'admin' };
  expiresAt: string | null;
}
export type LoginResponse = LoginOk | ApiErr;

export interface SubmitOk {
  ok: true;
  now: string;
  duplicate?: boolean;
  reading: {
    submissionId: string;
    equipmentName: string;
    location: string;
    operatorName: string;
    effectiveReadingAt: string;
    serverTimestamp?: string;
    abnormalStatus: 'normal' | 'warning' | 'critical';
    source: 'online' | 'offline';
  };
  schedule?: ScheduleState;
  nextRecommendedAt: string | null;
}
export type SubmitResponse = SubmitOk | ApiErr;

export interface Snapshot {
  ok: true;
  generatedAt: string;
  config: PublicConfig;
  /** Today's slot (1..config.pmsCycleLengthDays) in the rotating PMS calendar — computed server-side. */
  todayPmsCycleDay: number;
  equipment: Array<EquipmentRef & {
    /** 'daily' (hourly-cadence readings) or 'pms' (periodic preventive maintenance) — which of the two equipment lists this belongs in. */
    category: 'daily' | 'pms';
    /** PMS equipment only — the fixed day (1..config.pmsCycleLengthDays) this item is inspected on. null = not yet scheduled. */
    pmsCycleDay: number | null;
    schedule: import('../lib/schedule').ScheduleConfig;
    lastReadingAt?: string | null;
    lastValues?: Record<string, number | string>;
  }>;
  tags: Array<{ code: string; equipmentId: string }>;
  templates: Template[];
  thresholds: Array<Record<string, unknown>>;
}

export function isErr(r: { ok: boolean }): r is ApiErr {
  return r.ok === false;
}
